import { fromXPosToDate, useChartCtx } from '@weenat/client/dist/core/charts'
import { DataPoint } from '@weenat/client/dist/core/charts/d3-charts.type'
import { TimeSteps, underDayTimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { WeenatColor, fromColorPathToColor } from '@weenat/theme'
import { median } from 'd3-array'
import { ScaleLinear, ScaleTime } from 'd3-scale'
import { isNil } from 'lodash-es'
import moment from 'moment-timezone'
import React, { ReactNode, useCallback } from 'react'
import { useCursorContext } from './CursorContext'

const TEXT_CHARACTER_WIDTH = 8
const PADDING = 12
const spaceBetweenCursorAndTooltip = 8

export const TOOLTIP_HEIGHT = 22

interface DumbTooltipProps {
  tooltipWidth?: number
  tooltipHeight?: number
  color?: WeenatColor
  x: number
  y: number
  xScale: ScaleTime<number, number, never>
  yScale: ScaleLinear<number, number, never>
  chartWidth: number
  chartHeight: number
  renderTooltip?: () => ReactNode
  text: string
}

/**
 * Tooltip as dumb as possible. Should only handle positioning and rendering.
 * @param param0
 * @returns
 */
export const DumbTooltip: React.FC<DumbTooltipProps> = ({
  text,
  color = 'grayscale.white',
  renderTooltip,
  tooltipHeight = TOOLTIP_HEIGHT,
  tooltipWidth,
  x,
  y,
  xScale,
  yScale,
  chartWidth,
  chartHeight
}) => {
  const [maxY] = yScale.range()

  const letters = text.length ?? 0

  /** TOOLTIP POSITIONING */
  const horizontalCenter = median(xScale.range())
  const isAfterHorizontalMiddle = x > (horizontalCenter ?? 0)

  const rectWidth = !isNil(tooltipWidth)
    ? tooltipWidth
    : letters * TEXT_CHARACTER_WIDTH + PADDING * 2
  const halfWidth = rectWidth / 2

  const outsideOnRight = x + halfWidth > chartWidth
  const outsideOnLeft = x - halfWidth < 0

  const rectXPos = outsideOnLeft ? 0 : outsideOnRight ? -rectWidth : -halfWidth
  const rectTransform = `translate(${rectXPos}, 0)`

  const tooltipContent = renderTooltip?.()

  return !isNil(tooltipContent) ? (
    <g transform={`translate(${x}, 0)`}>
      <foreignObject
        x={
          isAfterHorizontalMiddle
            ? -rectWidth - spaceBetweenCursorAndTooltip
            : spaceBetweenCursorAndTooltip
        }
        y={(y / maxY) * (chartHeight - tooltipHeight)}
        width={tooltipWidth}
        height={tooltipHeight}
      >
        {tooltipContent}
      </foreignObject>
    </g>
  ) : !isNil(text) ? (
    <g transform={`translate(${x}, 0)`}>
      <g transform={rectTransform}>
        <rect
          y={4}
          rx={4}
          ry={4}
          height={TOOLTIP_HEIGHT}
          width={rectWidth}
          color={!isNil(color) ? fromColorPathToColor(color) : undefined}
        />
        <text x={halfWidth} y={20} fontVariant='tabular-nums' textAnchor='middle' fill='white'>
          {text}
        </text>
      </g>
    </g>
  ) : null
}

interface TooltipWithDefaultProps extends Omit<DumbTooltipProps, 'text'> {
  format?: string
  timeStep: TimeSteps
  invalid?: boolean
  invalidMessage?: string
}

export const TooltipWithDefault: React.FC<TooltipWithDefaultProps> = ({
  x,
  y,
  timeStep,
  xScale,
  yScale,
  invalid = false,
  invalidMessage,
  format = 'DD/MM/YYYY - HH:mm',
  color = 'grayscale.black',
  tooltipHeight,
  tooltipWidth: _tooltipWidth,
  renderTooltip,
  chartHeight,
  chartWidth
}) => {
  const date = fromXPosToDate(x, xScale)
  const isUnderDay = underDayTimeSteps.includes(timeStep)
  const formattedDate = moment(date).format(isUnderDay ? format : format.replace(' - HH:mm', ''))

  return (
    <DumbTooltip
      text={invalid && !isNil(invalidMessage) ? invalidMessage : formattedDate}
      chartHeight={chartHeight}
      chartWidth={chartWidth}
      tooltipHeight={tooltipHeight}
      tooltipWidth={_tooltipWidth}
      renderTooltip={renderTooltip}
      x={x}
      y={y}
      xScale={xScale}
      yScale={yScale}
      color={color}
    />
  )
}

interface TooltipWithCursorContextProps {
  timeStep: TimeSteps
  tooltipWidth: number
  tooltipHeight?: number
  xScale: ScaleTime<number, number, never>
  yScale: ScaleLinear<number, number, never>
  chartWidth: number
  chartHeight: number
  color?: WeenatColor
  format?: string
  invalid?: boolean
  invalidMessage?: string
  renderTooltip?: (dp: DataPoint) => ReactNode
}

export const TooltipWithCursorContext: React.FC<TooltipWithCursorContextProps> = ({
  timeStep,
  xScale,
  yScale,
  chartHeight,
  chartWidth,
  tooltipHeight,
  tooltipWidth,
  color,
  format,
  invalid,
  invalidMessage,
  renderTooltip
}) => {
  const { cursorPos, activeDataPoint } = useCursorContext()

  const renderCallback = useCallback(
    () => (!isNil(activeDataPoint) ? renderTooltip?.(activeDataPoint) : undefined),
    [renderTooltip, activeDataPoint]
  )

  if (isNil(cursorPos) || isNil(cursorPos.x) || isNil(cursorPos.y)) {
    return null
  }
  const { x, y } = cursorPos

  return (
    <TooltipWithDefault
      timeStep={timeStep}
      xScale={xScale}
      yScale={yScale}
      x={x}
      y={y}
      chartHeight={chartHeight}
      chartWidth={chartWidth}
      tooltipHeight={tooltipHeight}
      tooltipWidth={tooltipWidth}
      color={color}
      format={format}
      renderTooltip={!isNil(renderTooltip) ? renderCallback : undefined}
      invalid={invalid}
      invalidMessage={invalidMessage}
    />
  )
}

export interface TooltipProps {
  renderTooltip?: (dp: DataPoint) => ReactNode
  tooltipWidth?: number
  tooltipHeight?: number
  color?: WeenatColor
  format?: string
  timeStep: TimeSteps
  invalid?: boolean
  invalidMessage?: string
}

const Tooltip: React.FC<TooltipProps> = ({
  timeStep,
  renderTooltip,
  tooltipWidth = 144,
  tooltipHeight = 80,
  format = 'DD/MM/YYYY - HH:mm',
  color = 'grayscale.black',
  invalid,
  invalidMessage
}) => {
  const { height: chartHeight, xScale, yScale, width: chartWidth } = useChartCtx()

  return (
    <TooltipWithCursorContext
      timeStep={timeStep}
      xScale={xScale}
      yScale={yScale}
      chartHeight={chartHeight}
      chartWidth={chartWidth}
      tooltipHeight={tooltipHeight}
      tooltipWidth={tooltipWidth}
      color={color}
      format={format}
      renderTooltip={renderTooltip}
      invalid={invalid}
      invalidMessage={invalidMessage}
    />
  )
}

export default Tooltip
